import React, { useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import "./Navbar.css";
import UPPoliceLogo from "../assets/UP_Police.png";

function Navbar() {
  const { user, logout } = useContext(AuthContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showRequestsPopup, setShowRequestsPopup] = useState(false);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resolveDescription, setResolveDescription] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const location = useLocation();

  // Get API base URL from environment variables
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  // Fetch requests from backend
  const fetchRequests = async () => {
    if (!user || (user.role !== "admin" && user.role !== "officer")) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/requests/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch requests`);
      }

      const data = await response.json();
      console.log('Fetched requests:', data);
      setRequests(data || []);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load requests when component mounts or user changes
  useEffect(() => {
    fetchRequests();
  }, [user]);

  // Auto-refresh requests every 30 seconds
  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "officer")) {
      const interval = setInterval(fetchRequests, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleRequestsPopup = () => {
    setShowRequestsPopup(!showRequestsPopup);
    if (!showRequestsPopup) {
      fetchRequests(); // Refresh data when opening popup
    }
  };

  const handleResolveRequest = async (requestId, description = "") => {
    if (user.role !== "admin") {
      alert("Only admin can resolve requests");
      return;
    }

    if (!description.trim()) {
      alert("Please provide a response message for the resident.");
      return;
    }

    try {
      // Use the correct endpoint that matches your backend
      const response = await fetch(`${API_BASE_URL}/api/requests/${requestId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'resolved',
          admin_response: description,
          updated_by: user.id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || 'Failed to update request status';
        } catch {
          errorMessage = errorText || 'Failed to update request status';
        }
        throw new Error(errorMessage);
      }

      const updatedRequest = await response.json();
      
      // Update local state with the updated request
      setRequests(prevRequests => 
        prevRequests.map(request => 
          request.id === requestId 
            ? { 
                ...request, 
                ...updatedRequest,
                status: 'resolved',
                admin_response: description,
                resolved_by: user.name || user.username || 'Admin',
                resolved_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              } 
            : request
        )
      );

      // Close modal and reset
      setShowResolveModal(false);
      setSelectedRequestId(null);
      setResolveDescription("");
      
      alert('Request resolved successfully! Resident will be notified.');
      
      // Refresh the requests to get the latest data from backend
      setTimeout(() => fetchRequests(), 1000);
      
    } catch (err) {
      console.error('Error resolving request:', err);
      alert(`Failed to resolve request: ${err.message}`);
    }
  };

  const openResolveModal = (requestId) => {
    setSelectedRequestId(requestId);
    setShowResolveModal(true);
  };

  const closeResolveModal = () => {
    setShowResolveModal(false);
    setSelectedRequestId(null);
    setResolveDescription("");
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'status-badge pending';
      case 'in-progress':
        return 'status-badge in-progress';
      case 'resolved':
        return 'status-badge resolved';
      case 'rejected':
        return 'status-badge rejected';
      default:
        return 'status-badge pending';
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL}${imagePath}`;
  };

  // Count pending requests
  const pendingRequestsCount = requests.filter(r => r.status !== 'resolved').length;

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo on the left */}
        <div className="navbar-brand">
          <div 
            className="brand-icon"
            style={{ backgroundImage: `url(${UPPoliceLogo})` }}
          ></div>
        </div>

        {/* Title in the center */}
        <div className="navbar-title">
          <span className="brand-title">Police Lines Residential Portal</span>
          <span className="brand-subtitle">House Management System</span>
        </div>

        {/* Navigation links on the right */}
        <div className={`navbar-links ${isMenuOpen ? "active" : ""}`}>
          {/* Show Dashboard link only for admin role and only when not on dashboard */}
          {user && user.role === "admin" && location.pathname !== "/" && (
            <Link to="/" className="nav-link" onClick={() => setIsMenuOpen(false)}>
              <i className="fas fa-home"></i>Dashboard
            </Link>
          )}
          
          {/* Show Admin Panel link only for admin role and only when not on admin panel */}
          {user && user.role === "admin" && location.pathname !== "/admin" && (
            <Link to="/admin" className="nav-link" onClick={() => setIsMenuOpen(false)}>
              <i className="fas fa-cog"></i>Admin Panel
            </Link>
          )}

          {/* Show Accounts Dashboard link only for accounts role and only when not on accounts dashboard */}
          {user && user.role === "accounts" && location.pathname !== "/accounts" && (
            <Link to="/accounts" className="nav-link" onClick={() => setIsMenuOpen(false)}>
              <i className="fas fa-calculator"></i>Accounts
            </Link>
          )}

          {/* Requests button for admin and officer */}
          {(user && (user.role === "admin" || user.role === "officer")) && (
            <button className="nav-link requests-btn" onClick={toggleRequestsPopup}>
              <i className="fas fa-exclamation-circle"></i>Requests
              {pendingRequestsCount > 0 && (
                <span className="request-badge">
                  {pendingRequestsCount}
                </span>
              )}
            </button>
          )}
          
          {user ? (
            <button onClick={logout} className="btn-logout">
              <i className="fas fa-sign-out-alt"></i>Logout
            </button>
          ) : (
            <Link to="/login" className="nav-link" onClick={() => setIsMenuOpen(false)}>
              <i className="fas fa-sign-in-alt"></i>Login
            </Link>
          )}
        </div>

        {/* Requests Popup */}
        {showRequestsPopup && (
          <div className="requests-popup">
            <div className="popup-header">
              <h3>Requests & Complaints</h3>
              <div className="popup-actions">
                <button className="refresh-btn" onClick={fetchRequests} disabled={loading}>
                  <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                </button>
                <button className="close-btn" onClick={toggleRequestsPopup}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            
            <div className="popup-content">
              {loading && <div className="loading-message">Loading requests...</div>}
              
              {error && (
                <div className="error-message">
                  <p>Error: {error}</p>
                  <button onClick={fetchRequests} className="retry-btn">Retry</button>
                </div>
              )}
              
              {!loading && !error && requests.length === 0 && (
                <p className="no-requests">No requests to display.</p>
              )}
              
              {!loading && !error && requests.length > 0 && (
                <ul className="requests-list">
                  {requests.map(request => (
                    <li key={request.id} className={`request-item ${request.status?.toLowerCase() || 'pending'}`}>
                      <div className="request-content">
                        {/* Request Image - Enhanced Display */}
                        {request.image_url && (
                          <div className="request-image-container">
                            <img 
                              src={getImageUrl(request.image_url)} 
                              alt="Request"
                              className="request-thumbnail"
                              onClick={() => openImageModal(getImageUrl(request.image_url))}
                              onError={(e) => {
                                console.error('Failed to load image:', request.image_url);
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="150"%3E%3Crect width="150" height="150" fill="%23f0f0f0"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="12"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                              }}
                              crossOrigin="anonymous"
                            />
                            <div className="image-overlay">
                              <i className="fas fa-search-plus"></i>
                            </div>
                          </div>
                        )}
                        
                        <div className="request-details">
                          <h4>{request.description || 'No description provided'}</h4>
                          <div className="request-info">
                            <p><strong><i className="fas fa-user"></i> Resident:</strong> {request.resident_name || request.user_resident_name || request.quarter_resident_name || 'Unknown'}</p>
                            <p><strong><i className="fas fa-id-badge"></i> PNO:</strong> {request.resident_pno || request.quarter_pno || 'N/A'}</p>
                            {/* <p><strong><i className="fas fa-home"></i> Quarter No:</strong> {request.quarter_no || request.quarter_number || 'N/A'}</p> */}
                            <p><strong><i className="fas fa-phone"></i> Phone:</strong> {request.resident_phone || request.user_resident_phone || 'N/A'}</p>
                            <p><strong><i className="fas fa-tag"></i> Category:</strong> {request.category || 'General'}</p>
                            <p><strong><i className="fas fa-calendar"></i> Date:</strong> {new Date(request.created_at).toLocaleDateString('en-IN')}</p>
                            {request.resolved_at && (
                              <p><strong><i className="fas fa-check-circle"></i> Resolved:</strong> {new Date(request.resolved_at).toLocaleDateString('en-IN')}</p>
                            )}
                          </div>
                          
                          <span className={getStatusBadgeClass(request.status)}>
                            <i className={`fas ${request.status === 'resolved' ? 'fa-check-circle' : request.status === 'in-progress' ? 'fa-spinner' : 'fa-clock'}`}></i>
                            {request.status || 'Pending'}
                          </span>
                          
                          {request.admin_response && (
                            <div className="admin-response">
                              <strong><i className="fas fa-reply"></i> Admin Response:</strong>
                              <p>{request.admin_response}</p>
                              {request.resolved_by && (
                                <small><em><i className="fas fa-user-shield"></i> Resolved by: {request.resolved_by}</em></small>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Action buttons - only for admin, officer can only view */}
                        {user.role === "admin" && request.status !== "resolved" && (
                          <div className="request-actions">
                            <button 
                              className="resolve-btn"
                              onClick={() => openResolveModal(request.id)}
                            >
                              <i className="fas fa-check"></i> Resolve
                            </button>
                          </div>
                        )}
                        
                        {/* View only indicator for officers */}
                        {user.role === "officer" && (
                          <div className="officer-view-badge">
                            <i className="fas fa-eye"></i> View Only
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {/* Image Modal - Full Screen View */}
        {showImageModal && selectedImage && (
          <div className="image-modal-overlay" onClick={closeImageModal}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="image-modal-close" onClick={closeImageModal}>
                <i className="fas fa-times"></i>
              </button>
              <img 
                src={selectedImage} 
                alt="Full size request" 
                crossOrigin="anonymous"
              />
              <div className="image-modal-actions">
                <a 
                  href={selectedImage} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="image-modal-link"
                >
                  <i className="fas fa-external-link-alt"></i> Open in New Tab
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Resolve Modal */}
        {showResolveModal && (
          <div className="modal-overlay">
            <div className="resolve-modal">
              <div className="modal-header">
                <h3><i className="fas fa-check-circle"></i> Resolve Request</h3>
                <button className="close-btn" onClick={closeResolveModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              
              <div className="modal-content">
                <div className="request-summary">
                  {selectedRequestId && (
                    <div className="selected-request-info">
                      <h4>Request Details:</h4>
                      {(() => {
                        const request = requests.find(r => r.id === selectedRequestId);
                        return request ? (
                          <div>
                            <p><strong>ID:</strong> #{request.id}</p>
                            <p><strong>Category:</strong> {request.category}</p>
                            <p><strong>Description:</strong> {request.description}</p>
                            <p><strong>Resident:</strong> {request.resident_name}</p>
                            <p><strong>Quarter No:</strong> {request.quarter_no || 'N/A'}</p>
                            {request.image_url && (
                              <div className="modal-request-image">
                                <img 
                                  src={getImageUrl(request.image_url)} 
                                  alt="Request"
                                  onClick={() => openImageModal(getImageUrl(request.image_url))}
                                  style={{ cursor: 'pointer', maxWidth: '200px', borderRadius: '4px' }}
                                  crossOrigin="anonymous"
                                />
                              </div>
                            )}
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="resolveDescription">
                    Response Message to Resident: <span className="required">*</span>
                  </label>
                  <textarea
                    id="resolveDescription"
                    value={resolveDescription}
                    onChange={(e) => setResolveDescription(e.target.value)}
                    placeholder="Enter a detailed response that will be sent to the resident. This message will be visible when they check their request status..."
                    rows={5}
                    maxLength="1000"
                    required
                  />
                  <small>{resolveDescription.length}/1000 characters</small>
                </div>
                
                <div className="modal-actions">
                  <button 
                    className="btn-resolve"
                    onClick={() => handleResolveRequest(selectedRequestId, resolveDescription)}
                    disabled={!resolveDescription.trim()}
                  >
                    <i className="fas fa-check"></i> Mark as Resolved
                  </button>
                  <button className="btn-cancel" onClick={closeResolveModal}>
                    <i className="fas fa-times"></i> Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="hamburger-menu" onClick={toggleMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;